import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { formatCustomerPhone } from "../../shared/format";
import {
  nyTodayDate,
  stepApptDate,
  formatApptDateLabel,
  type ZohoAppt,
  type ZohoContact,
} from "../../shared/zohoImport";

// Full US state + territory names → USPS 2-letter codes. Nominatim returns
// the full name in `address.state`; we shorten so the State field holds the
// 2-letter code the rest of the app expects.
const US_STATE_ABBR: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY",
  "puerto rico": "PR", "u.s. virgin islands": "VI", guam: "GU",
  "american samoa": "AS", "northern mariana islands": "MP",
};

// Email-domain autocomplete — same list and logic as the desktop Customer
// card (App.tsx EMAIL_DOMAINS / getEmailSuggestions). Once the user types "@",
// suggest the common domains as full addresses; tapping one fills the field.
const EMAIL_DOMAINS = [
  "gmail.com",
  "icloud.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
];

function getEmailSuggestions(input: string): string[] {
  const v = (input || "").trim();
  const at = v.indexOf("@");
  if (at < 0) return [];
  const name = v.slice(0, at);
  const partial = v.slice(at + 1).toLowerCase();
  if (!name) return [];
  return EMAIL_DOMAINS.filter((d) => d.startsWith(partial))
    .slice(0, 5)
    .map((d) => `${name}@${d}`);
}

type NominatimAddress = {
  town?: string;
  city?: string;
  village?: string;
  hamlet?: string;
  suburb?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country_code?: string;
};

/** Split a reverse-geocode result into separate City + State (2-letter) parts,
 *  matching the new model where clientTown=City and clientState are distinct. */
function parseTownState(addr: NominatimAddress | undefined): {
  town: string;
  state: string;
} {
  if (!addr) return { town: "", state: "" };
  const town =
    addr.town || addr.city || addr.village || addr.hamlet || addr.suburb || "";
  const stateRaw = addr.state || "";
  const isUS = (addr.country_code || "").toLowerCase() === "us";
  const state = isUS
    ? US_STATE_ABBR[stateRaw.toLowerCase()] || stateRaw
    : stateRaw;
  return { town, state };
}

// 9-field client model (desktop parity). clientTitle was removed app-wide —
// the greeting now uses clientFirstName. clientLocation is the STREET address;
// clientTown is the City (kept separate from clientState/clientZip).
export type CustomerFields = {
  clientFirstName: string;
  clientLastName: string;
  clientLocation: string; // street
  clientTown: string; // city
  clientState: string;
  clientZip: string;
  clientPhone: string;
  clientEmail: string;
};

type Props = {
  value: CustomerFields;
  onChange: (next: CustomerFields) => void;
  /** When true, draws the blue "active step" outline. Parent decides
   *  which card is currently active in the guided flow. */
  active?: boolean;
  /** True only for the allow-listed admin email — shows the Zoho import
   *  entry. The edge function enforces the same allow-list server-side. */
  canImport?: boolean;
  /** Live connectivity. Import is online-only; when false the entry is
   *  shown but disabled with a "connect to import" hint. */
  isOnline?: boolean;
};

/**
 * Mobile Customer Card.
 * Matches the desktop 9-field model: clientFirstName, clientLastName,
 * clientLocation (street), clientTown (city), clientState, clientZip,
 * clientPhone, clientEmail. Drafts saved on mobile open cleanly on desktop.
 *
 * A chevron in the header collapses the card to a thin one-line summary bar
 * (last name · city · email); tapping the bar or the chevron re-expands it.
 * Defaults to expanded on a new estimate.
 *
 * For the allow-listed admin (canImport), an "Import from Zoho" entry opens
 * a picker of today's appointments and fills the fields from the linked
 * contact — the mobile twin of desktop's App.tsx importer.
 */
export default function CustomerCard({
  value,
  onChange,
  active = false,
  canImport = false,
  isOnline = true,
}: Props) {
  const isComplete =
    !!value.clientLastName.trim() &&
    !!value.clientTown.trim() &&
    !!value.clientEmail.trim();

  // Collapse is manual (chevron in the header). Default expanded on a new
  // estimate so it's obvious where to enter info; the user collapses to the
  // thin summary bar once they've filled in what they need.
  const [collapsed, setCollapsed] = useState(false);

  // One-line summary for the collapsed bar: last name · city, skipping any
  // blanks so there are no stray separators. Empty → muted placeholder.
  const summaryText = [value.clientLastName, value.clientTown]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" · ");

  const set =
    <K extends keyof CustomerFields>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onChange({ ...value, [k]: e.target.value });

  const setPhone = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({
      ...value,
      clientPhone: formatCustomerPhone(e.target.value, value.clientPhone),
    });

  // Email-domain autocomplete (mobile twin of desktop). Open the dropdown once
  // there's an "@"; tapping a suggestion fills the full address.
  const [emailSugOpen, setEmailSugOpen] = useState(false);
  const emailSuggestions = getEmailSuggestions(value.clientEmail);

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange({ ...value, clientEmail: v });
    setEmailSugOpen(v.includes("@"));
  };

  const pickEmailSuggestion = (s: string) => {
    onChange({ ...value, clientEmail: s });
    setEmailSugOpen(false);
  };

  // -----------------------------------------------------------------
  // "Use my location" — tap-only. GPS → Nominatim → City + State.
  // Tap-only because GPS = the user's current spot, which only equals
  // the customer's town when the user is on-site.
  // -----------------------------------------------------------------
  const [geoStatus, setGeoStatus] = useState<"idle" | "loading">("idle");
  const [geoMessage, setGeoMessage] = useState<string>("");
  const valueRef = useRef(value);
  valueRef.current = value;
  const messageTimerRef = useRef<number | null>(null);

  const flashMessage = (msg: string) => {
    setGeoMessage(msg);
    if (messageTimerRef.current) window.clearTimeout(messageTimerRef.current);
    messageTimerRef.current = window.setTimeout(() => {
      setGeoMessage("");
      messageTimerRef.current = null;
    }, 4000);
  };

  useEffect(() => {
    return () => {
      if (messageTimerRef.current) window.clearTimeout(messageTimerRef.current);
    };
  }, []);

  const useMyLocation = () => {
    if (geoStatus === "loading") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      flashMessage("Location not supported — type the city manually");
      return;
    }
    setGeoStatus("loading");
    setGeoMessage("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const url =
            `https://nominatim.openstreetmap.org/reverse?format=json` +
            `&lat=${encodeURIComponent(latitude)}` +
            `&lon=${encodeURIComponent(longitude)}` +
            `&zoom=10&addressdetails=1`;
          const res = await fetch(url, {
            headers: { "Accept-Language": "en" },
          });
          if (!res.ok) throw new Error(`status ${res.status}`);
          const json = (await res.json()) as { address?: NominatimAddress };
          const { town, state } = parseTownState(json?.address);
          if (!town && !state) {
            flashMessage("Couldn't read a city from your location");
            setGeoStatus("idle");
            return;
          }
          onChange({
            ...valueRef.current,
            clientTown: town || valueRef.current.clientTown,
            clientState: state || valueRef.current.clientState,
          });
          setGeoStatus("idle");
        } catch {
          flashMessage("Lookup failed — type the city manually");
          setGeoStatus("idle");
        }
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied — type the city manually"
            : err.code === err.TIMEOUT
            ? "Location timed out — try again or type the city"
            : "Location unavailable — type the city manually";
        flashMessage(msg);
        setGeoStatus("idle");
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  };

  // -----------------------------------------------------------------
  // Zoho import — mobile twin of desktop's openZohoImport /
  // pickZohoAppointment (App.tsx). Same `zoho-appointments` edge
  // function, same auth (the shared supabase client carries the JWT).
  // -----------------------------------------------------------------
  const [zohoOpen, setZohoOpen] = useState(false);
  const [zohoLoading, setZohoLoading] = useState(false);
  const [zohoAppts, setZohoAppts] = useState<ZohoAppt[]>([]);
  const [zohoResolvingId, setZohoResolvingId] = useState<string | null>(null);
  const [zohoError, setZohoError] = useState<string>("");
  // Selected day for the picker ("YYYY-MM-DD", NY). Defaults to today on open;
  // the ‹ / › steppers move it and re-fetch that day's appointments.
  const [zohoDate, setZohoDate] = useState<string>(() => nyTodayDate());

  const clientFieldsHaveData = (): boolean =>
    [
      value.clientFirstName,
      value.clientLastName,
      value.clientLocation,
      value.clientTown,
      value.clientState,
      value.clientZip,
      value.clientPhone,
      value.clientEmail,
    ].some((v) => (v || "").trim() !== "");

  // "2026-06-12T15:00:00-04:00" → "3:00 PM"
  const formatApptTime = (iso: string): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  // Fetch the "list" action for a given NY day ("YYYY-MM-DD") into the picker.
  const loadZohoApptsForDate = async (date: string) => {
    setZohoLoading(true);
    setZohoError("");
    setZohoAppts([]);
    try {
      const { data, error } = await supabase.functions.invoke(
        "zoho-appointments",
        { body: { action: "list", date } }
      );
      if (error) throw error;
      const items: ZohoAppt[] = Array.isArray((data as any)?.items)
        ? (data as any).items
        : [];
      setZohoAppts(items);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Zoho list failed", e);
      setZohoError("Couldn't reach Zoho — try again or enter client info manually");
    } finally {
      setZohoLoading(false);
    }
  };

  // Open the picker on today's appointments (default day = today, NY).
  const openZohoImport = async () => {
    if (!isOnline) return; // entry is disabled too; belt-and-suspenders
    const today = nyTodayDate();
    setZohoDate(today);
    setZohoOpen(true);
    await loadZohoApptsForDate(today);
  };

  // Step the selected day back/forward and re-fetch (blocked mid-resolve).
  // Sundays are skipped in both directions (no Sunday meetings).
  const stepZohoDate = (delta: number) => {
    if (zohoResolvingId) return;
    const next = stepApptDate(zohoDate, delta);
    setZohoDate(next);
    void loadZohoApptsForDate(next);
  };

  const pickZohoAppointment = async (appt: ZohoAppt) => {
    if (zohoResolvingId) return; // a resolve is already in flight
    if (clientFieldsHaveData()) {
      const ok = window.confirm(
        "The Customer fields already have info. Replace them with this appointment's contact?"
      );
      if (!ok) return;
    }
    setZohoResolvingId(appt.contactId);
    setZohoError("");
    try {
      const { data, error } = await supabase.functions.invoke(
        "zoho-appointments",
        {
          body: {
            action: "resolve",
            id: appt.contactId,
            module: appt.contactModule,
          },
        }
      );
      if (error) throw error;
      const c = (data as any)?.contact as ZohoContact | undefined;
      if (!c) throw new Error("No contact returned");

      onChange({
        clientFirstName: c.firstName || "",
        clientLastName: c.lastName || "",
        clientLocation: c.street || "",
        clientTown: c.city || "",
        clientState: c.state || "",
        clientZip: c.zip || "",
        // prev="" → clean format from scratch.
        clientPhone: formatCustomerPhone(c.phone || "", ""),
        clientEmail: c.email || "",
      });

      setZohoOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Zoho resolve failed", e);
      setZohoError("Couldn't reach Zoho — enter client info manually");
    } finally {
      setZohoResolvingId(null);
    }
  };

  const closeZoho = () => {
    if (zohoResolvingId) return; // don't close mid-resolve
    setZohoOpen(false);
  };

  const titleNode = (
    <h3 className="m-card-title m-card-title-row">
      <span>Customer</span>
      {isComplete && (
        <span className="m-line-check" aria-label="Customer info complete">
          ✓
        </span>
      )}
      <button
        type="button"
        className="m-customer-collapse-btn"
        onClick={() => setCollapsed(true)}
        aria-expanded={true}
        aria-label="Collapse customer details"
      >
        <span className="m-customer-chevron" aria-hidden>
          ⌄
        </span>
      </button>
    </h3>
  );

  const sectionClass =
    "m-card m-customer-card" + (active ? " is-active-step" : "");

  // The "Import from Zoho" entry + its offline hint. Rendered at the top of
  // the editing view for the allow-listed admin only.
  const importEntry = canImport ? (
    <div className="m-zoho-entry">
      <button
        type="button"
        className="m-zoho-import-btn"
        onClick={openZohoImport}
        disabled={!isOnline}
        aria-label="Import customer from Zoho"
      >
        <span aria-hidden>⤓</span> Import from Zoho
      </button>
      {!isOnline && (
        <div className="m-zoho-offline-hint">
          Connect to the internet to import — or enter info manually.
        </div>
      )}
    </div>
  ) : null;

  const zohoPicker =
    zohoOpen && canImport ? (
      <div
        className="m-zoho-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label="Today's Zoho appointments"
        onClick={closeZoho}
      >
        <div className="m-zoho-sheet" onClick={(e) => e.stopPropagation()}>
          <div className="m-send-grabber" aria-hidden />
          <header className="m-zoho-head">
            <div>
              <h2 className="m-zoho-title">Zoho appointments</h2>
              <div className="m-zoho-subtitle">
                Tap an appointment to fill the Customer fields from its
                linked contact.
              </div>
            </div>
            <button
              type="button"
              className="m-send-close"
              onClick={closeZoho}
              aria-label="Close"
              disabled={!!zohoResolvingId}
            >
              ✕
            </button>
          </header>

          <div className="m-zoho-datenav">
            <button
              type="button"
              className="m-zoho-date-arrow"
              onClick={() => stepZohoDate(-1)}
              disabled={zohoLoading || !!zohoResolvingId}
              aria-label="Previous day"
            >
              ‹
            </button>
            <span className="m-zoho-date-label">
              {formatApptDateLabel(zohoDate)}
            </span>
            <button
              type="button"
              className="m-zoho-date-arrow"
              onClick={() => stepZohoDate(1)}
              disabled={zohoLoading || !!zohoResolvingId}
              aria-label="Next day"
            >
              ›
            </button>
          </div>

          <div className="m-zoho-list">
            {zohoLoading ? (
              <div className="m-zoho-empty">
                <span className="m-geo-spinner" aria-hidden>
                  ⟳
                </span>{" "}
                Loading appointments…
              </div>
            ) : zohoError ? (
              <div className="m-zoho-empty">{zohoError}</div>
            ) : zohoAppts.length === 0 ? (
              <div className="m-zoho-empty">
                No appointments with a linked contact on{" "}
                {formatApptDateLabel(zohoDate)}
              </div>
            ) : (
              zohoAppts.map((appt) => {
                const resolving = zohoResolvingId === appt.contactId;
                const busy = !!zohoResolvingId;
                return (
                  <button
                    key={appt.eventId}
                    type="button"
                    className="m-zoho-row"
                    disabled={busy}
                    onClick={() => pickZohoAppointment(appt)}
                  >
                    <span className="m-zoho-row-time">
                      {formatApptTime(appt.startTime) || "—"}
                    </span>
                    <span className="m-zoho-row-title">{appt.title}</span>
                    {resolving && (
                      <span className="m-geo-spinner" aria-hidden>
                        ⟳
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    ) : null;

  if (collapsed) {
    return (
      <section className={sectionClass} aria-label="Customer">
        {/* Thin summary bar — tapping anywhere on it (or the chevron) re-expands. */}
        <button
          type="button"
          className="m-customer-collapsed-bar"
          onClick={() => setCollapsed(false)}
          aria-expanded={false}
          aria-label="Expand customer details"
        >
          <span className="m-customer-collapsed-label">Customer</span>
          <span
            className={
              "m-customer-collapsed-summary" +
              (summaryText ? "" : " is-placeholder")
            }
          >
            {summaryText || "No customer info yet."}
          </span>
          <span className="m-customer-chevron is-collapsed" aria-hidden>
            ⌄
          </span>
        </button>
        {zohoPicker}
      </section>
    );
  }

  return (
    <section className={sectionClass} aria-label="Customer">
      {titleNode}

      {importEntry}

      <div className="m-field-row">
        <div className="m-field">
          <label className="m-section-label" htmlFor="m-c-first">
            First Name
          </label>
          <input
            id="m-c-first"
            className="m-input"
            type="text"
            autoCapitalize="words"
            autoComplete="given-name"
            value={value.clientFirstName}
            onChange={set("clientFirstName")}
          />
        </div>

        <div className="m-field">
          <label className="m-section-label" htmlFor="m-c-last">
            Last Name
          </label>
          <input
            id="m-c-last"
            className="m-input"
            type="text"
            autoCapitalize="words"
            autoComplete="family-name"
            value={value.clientLastName}
            onChange={set("clientLastName")}
          />
        </div>
      </div>

      <div className="m-field">
        <label className="m-section-label" htmlFor="m-c-street">
          Street
        </label>
        <input
          id="m-c-street"
          className="m-input"
          type="text"
          autoCapitalize="words"
          autoComplete="address-line1"
          placeholder="123 Main St"
          value={value.clientLocation}
          onChange={set("clientLocation")}
        />
      </div>

      <div className="m-field">
        <label className="m-section-label" htmlFor="m-c-town">
          City
        </label>
        <div className="m-location-row">
          <input
            id="m-c-town"
            className="m-input"
            type="text"
            autoCapitalize="words"
            autoComplete="address-level2"
            placeholder="Commack"
            value={value.clientTown}
            onChange={set("clientTown")}
          />
          <button
            type="button"
            className="m-geo-btn"
            onClick={useMyLocation}
            disabled={geoStatus === "loading"}
            aria-label="Use my current location"
            title="Use my current location"
          >
            {geoStatus === "loading" ? (
              <span className="m-geo-spinner" aria-hidden>⟳</span>
            ) : (
              <span aria-hidden>📍</span>
            )}
          </button>
        </div>
        {geoMessage && (
          <div className="m-geo-message" role="status">
            {geoMessage}
          </div>
        )}
      </div>

      <div className="m-field-row">
        <div className="m-field" style={{ flex: "0 0 38%" }}>
          <label className="m-section-label" htmlFor="m-c-state">
            State
          </label>
          <input
            id="m-c-state"
            className="m-input"
            type="text"
            autoCapitalize="characters"
            autoComplete="address-level1"
            placeholder="NY"
            value={value.clientState}
            onChange={set("clientState")}
          />
        </div>

        <div className="m-field">
          <label className="m-section-label" htmlFor="m-c-zip">
            Zip
          </label>
          <input
            id="m-c-zip"
            className="m-input"
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="11725"
            value={value.clientZip}
            onChange={set("clientZip")}
          />
        </div>
      </div>

      <div className="m-field">
        <label className="m-section-label" htmlFor="m-c-phone">
          Phone
        </label>
        <input
          id="m-c-phone"
          className="m-input"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          placeholder="(516) 302-4510"
          value={value.clientPhone}
          onChange={setPhone}
        />
      </div>

      <div className="m-field">
        <label className="m-section-label" htmlFor="m-c-email">
          Email
        </label>
        <div className="m-email-suggest-wrap">
          <input
            id="m-c-email"
            className="m-input"
            type="email"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            inputMode="email"
            value={value.clientEmail}
            onChange={onEmailChange}
            onFocus={() => {
              if (value.clientEmail.includes("@")) setEmailSugOpen(true);
            }}
            onBlur={() => {
              // Defer close so a tap on a suggestion registers first.
              window.setTimeout(() => setEmailSugOpen(false), 120);
            }}
          />
          {emailSugOpen && emailSuggestions.length > 0 && (
            <div className="m-email-suggest" role="listbox">
              {emailSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="m-email-suggest-item"
                  onMouseDown={(e) => e.preventDefault()} // keep input focused
                  onClick={() => pickEmailSuggestion(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {zohoPicker}
    </section>
  );
}
